import { useDispatch, useSelector } from 'react-redux'
import { contractsSelectors, contractsThunks } from '@/entity'
import { Button, DataGridTable, Field, Title } from '@/ui'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field as FormikField } from 'formik'
import { FormControlLabel, RadioGroup } from '@mui/material'
import { Radio } from '@/entity/contracts/ui/form/components'
import React, { useEffect, useRef } from 'react'
import { handbookThunks } from '@/entity/handbook'
import cl from 'classnames'
import * as Yup from 'yup'
import { DigitalSign } from '@/utils/digitalSign'
import { message } from 'antd'
import { earlyExitLeaveSave } from '@/entity/contracts/model/thunks'

const FORM_NAME = {
	bin: 'bin',
	earlyDaysOfCount: 'earlyDaysOfCount',
	earlyEndDate: 'earlyEndDate',
	earlyWorkDate: 'earlyWorkDate',
	earlyreasonKz: 'earlyreasonKz',
	earlyreasonRu: 'earlyreasonRu',
	socialLeaveExternalId: 'socialLeaveExternalId',
}



export const SocialEarlyExitForm = ({socialLeave, onClose, page, pageSize, filters}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const formRef = useRef(null)
	const digitalSign = new DigitalSign()

	const {contract, socialLeaveTypes} = useSelector((state) => ({
		contract: state.contracts.contract?.data,
		socialLeaveTypes: state.handbook?.socialLeaveTypes,
	}))

	let initialState = {
		bin: contract.bin,
		earlyEndDate: socialLeave?.earlyExitSocialLeave?.earlyEndDate,
		earlyreasonKz: socialLeave?.earlyExitSocialLeave?.earlyreasonKz || '',
		earlyreasonRu: socialLeave?.earlyExitSocialLeave?.earlyreasonRu || '',
		socialLeaveExternalId: socialLeave.externalId,

		...(socialLeave?.dleaveTypeCode?.value !== '2' ? {
			earlyWorkDate: socialLeave?.earlyExitSocialLeave?.earlyWorkDate,
			earlyDaysOfCount: socialLeave?.earlyExitSocialLeave?.earlyDaysOfCount,
		} : {})
	}

	const ValidationSchema = Yup.object().shape({
		bin: Yup.string().required({key: 'required'}).nullable(),
		earlyEndDate: Yup.string().required({key: 'required'}).nullable(),
		earlyreasonKz: Yup.string().required({key: 'required'}).nullable(),
		earlyreasonRu: Yup.string().required({key: 'required'}).nullable(),

		...(socialLeave?.dleaveTypeCode?.value !== '2' ? {
			earlyWorkDate: Yup.string().required({key: 'required'}).nullable(),
			earlyDaysOfCount: Yup.string().required({key: 'required'}).nullable(),
		} : {})
	})

	useEffect(() => {
		if (!socialLeaveTypes?.length > 0) dispatch(handbookThunks.getSocialLeaveTypes())
	}, [])

	const headData = [
		{ title: t('worker_iin'), data: contract?.iin },
		{ title: t('personal_short_information'), data: contract?.fullName },
	]

	const handleSaveSign = (values) => {
		let body = {
			data: values,
			onSuccess: async (xml) => {
				const res = await digitalSign.handleSend(xml)

				let bodyXml = {
					data: {
						signedXml: res,
						externalId: null,
						entityId: null,
						paramMap: values,
					},
					onSuccess: () => {
						dispatch(contractsThunks.getContracts(filters, page, pageSize))
						dispatch(contractsThunks.getContract(contract?.externalId, contract?.isLocalContract))
						message.success('Операция выполнена успешно', 10)
					}
				}

				dispatch(contractsThunks.sendSignedEarlyExitSocialLeave(bodyXml))
			}
		}
		dispatch(contractsThunks.earlyExitLeaveXml(body))
	}

	const handleSave = () => {
		let body = {
			data: formRef?.current?.values,
			onSuccess: () => {
				dispatch(contractsThunks.getContract(contract?.externalId, contract?.isLocalContract))
				message.success('Операция выполнена успешно', 10)
			}
		}

		dispatch(earlyExitLeaveSave(body))
	}

	return (
		<div>
			<DataGridTable hideSubTitle hideTitle information={headData}/>

			<Title className={cl('py-4')}>
				{t('vacation_info')}
			</Title>

			<strong className={cl('pb-4')}>
				{t('social_leave.form.subtitle')}
			</strong>

			<Formik initialValues={initialState} onSubmit={(values) => handleSaveSign(values)} innerRef={formRef} validationSchema={ValidationSchema}>
				{() => {
					return (
						<Form className={cl('flex', 'flex-column', 'gap-4')}>
							<RadioGroup>
								<FormControlLabel
									control={<Radio />}
									label={t('social_leave.form.earlyExit.information', {
										start_date: socialLeave?.beginDate,
										end_date: socialLeave?.endDate,
										leave_date: socialLeave?.firstDayDate,
									})}
									checked={true}
								/>
							</RadioGroup>

							<Field
								fieldType="select"
								label={t('soc_vacation_type')}
								placeholder={t('choose_from_directory')}
								options={socialLeaveTypes}
								disabled
								value={socialLeave?.dleaveTypeCode?.value}
							/>

							<div className={cl('flex', 'gap-4')}>
								<div className={cl('flex-1')}>
									<Field
										fieldType="datePicker"
										label={t('vacation_date_start')}
										placeholder={t('default_date_value')}
										maxDate={new Date()}
										disabled
										value={socialLeave?.beginDate}
									/>
								</div>

								<div className={cl('flex-1')}>
									<Field
										fieldType="datePicker"
										label={t('vacation_date_end')}
										placeholder={t('default_date_value')}
										maxDate={new Date()}
										disabled
										value={socialLeave?.endDate}
									/>
								</div>

								{
									socialLeave?.dleaveTypeCode?.value !== '2' &&
									<div className={cl('flex-1')}>
										<Field
											fieldType="datePicker"
											label={t('vacation_date_work_start')}
											placeholder={t('default_date_value')}
											maxDate={new Date()}
											disabled
											value={socialLeave?.firstDayDate}
										/>
									</div>
								}

							</div>

							{
								socialLeave?.dleaveTypeCode?.value !== '2' &&
								<Field
									type="number"
									min={0}
									hideNumberArrows
									label={t('incapacity_days')}
									placeholder={t('set_weekends_count')}
									disabled
									value={socialLeave?.daysOff}
								/>
							}


							<div className={cl('flex', 'gap-4')}>
								<div className={cl({
									['flex-1']: socialLeave?.dleaveTypeCode?.value !== '2'
								})}>
									<FormikField name={FORM_NAME.earlyEndDate}>
										{({ field, meta: { touched, error } }) => (
											<Field
												fieldType="datePicker"
												label={t('social_leave.fact_work_date_label')}
												placeholder={t('default_date_value')}
												error={touched && error}
												hint={t('social_leave.fact_work_date_hint')}
												isRequired
												{...field}
											/>
										)}
									</FormikField>
								</div>

								{
									socialLeave?.dleaveTypeCode?.value !== '2' &&
									<div className={cl('flex-1')}>
										<FormikField name={FORM_NAME.earlyWorkDate}>
											{({ field, meta: { touched, error } }) => (
												<Field
													fieldType="datePicker"
													label={t('vacation_date_work_start')}
													placeholder={t('default_date_value')}
													hint={t('vacation_date_work_start_hint')}
													error={touched && error}
													isRequired
													{...field}
												/>
											)}
										</FormikField>
									</div>
								}

								{
									socialLeave?.dleaveTypeCode?.value !== '2' &&
									<div className={cl('flex-1')}>
										<FormikField name={FORM_NAME.earlyDaysOfCount}>
											{({ field, meta: { touched, error } }) => (
												<Field
													{...field}
													error={touched && error}
													type="number"
													min={0}
													hideNumberArrows
													label={t('incapacity_days')}
													placeholder={t('set_weekends_count')}
													isRequired
												/>
											)}
										</FormikField>
									</div>
								}

							</div>

							<FormikField name={FORM_NAME.earlyreasonKz}>
								{({ field, meta: { touched, error } }) => (
									<Field
										fieldType={'textarea'}
										label={t('social_leave.form.earlyExit.reason_kz')}
										placeholder={t('social_leave.form.earlyExit.reason_placeholder')}
										error={touched && error}
										showCount
										maxLength={4000}
										rows={3}
										isRequired
										{...field}
									/>
								)}
							</FormikField>

							<FormikField name={FORM_NAME.earlyreasonRu}>
								{({ field, meta: { touched, error } }) => (
									<Field
										fieldType={'textarea'}
										label={t('social_leave.form.earlyExit.reason_ru')}
										placeholder={t('social_leave.form.earlyExit.reason_placeholder')}
										error={touched && error}
										showCount
										maxLength={4000}
										rows={3}
										isRequired
										{...field}
									/>
								)}
							</FormikField>

							<div className={cl('flex', 'gap-4', 'align-items-center', 'justify-content-end')}>
								<Button type="bare" onClick={onClose}>
									{t('cancel')}
								</Button>
								{
									socialLeave?.earlyExitSocialLeave?.status !== 'SIGNED' &&

									<Button onClick={handleSave}>{t('save')}</Button>
								}
								<Button type="submit">{t('save_and_sign')}</Button>
							</div>
						</Form>
					)
				}}
			</Formik>
		</div>
	)
}